/**
 * Health & Vulnerability Analysis v2.
 *
 * Layers:
 *   - Disclaimer banner (top + bottom — non-dismissible safety floor)
 *   - Layer 1: 5 plain-language takeaways
 *   - Prakṛti card with vata/pitta/kapha percentages
 *   - Vulnerability map (12 body areas)
 *   - Mental & emotional notes
 *   - Chronic-risk lines (severity-coloured)
 *   - Dasha-period view + 12-month transit watchlist
 *   - Ahimsa-aligned lifestyle prescriptions (5 categories)
 *   - Annual recap
 *   - Methodology footer (financial-disclaimer tier) citing
 *     Kalyāṇakāraka, BPHS, Charaka Saṃhitā, Ashtanga Hridayam
 *
 * Hindi/English toggle drives both the rendered report and the PDF.
 */
import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type {
  AhPrescriptionEntry,
  ChartCreateRequest,
  HealthLineEntry,
  HealthVulnerabilityResponse,
  LifestylePrescriptionEntry,
  PrakritiBreakdownEntry,
  VulnerabilityRowEntry,
} from '@/api/types';
import { ChartInputForm } from '@/components/ChartInputForm';
import { useLang } from '@/i18n/useLang';

export function HealthPage() {
  const { lang, setLang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const [submittedReq, setSubmittedReq] = useState<ChartCreateRequest | null>(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  const reportMut = useMutation<HealthVulnerabilityResponse, Error, ChartCreateRequest>({
    mutationFn: (req) => api.healthReport(req),
  });

  const onFormSubmit = useCallback(
    (req: ChartCreateRequest) => {
      setSubmittedReq(req);
      reportMut.mutate(req);
    },
    [reportMut],
  );

  const submitError = useMemo(() => {
    if (!reportMut.error) return null;
    const e = reportMut.error;
    if (e instanceof ApiError) {
      if (e.status === 422) return t('अमान्य इनपुट', 'Invalid input');
      if (e.status === 401) return t('फिर से प्रवेश करें', 'Please sign in again');
    }
    return e.message ?? 'Error';
  }, [reportMut.error, lang]);

  const report = reportMut.data;

  const downloadPdf = useCallback(async () => {
    if (!submittedReq) return;
    try {
      setPdfDownloading(true);
      const blob = await api.healthReportPdfBlob(submittedReq, lang);
      const name = (report?.input.name ?? 'health').replace(/\s+/g, '_');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `health_${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('PDF download failed', e);
      alert(t('PDF डाउनलोड में त्रुटि', 'PDF download failed'));
    } finally {
      setPdfDownloading(false);
    }
  }, [submittedReq, lang, report, t]);

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 p-4 lg:gap-6 lg:p-6">
      <header className="flex items-start justify-between gap-3 rounded-2xl bg-stone-900/80 px-4 py-3 ring-1 ring-stone-800">
        <div>
          <h1 className="text-lg font-semibold text-saffron-100">
            {t('स्वास्थ्य एवं प्रकृति विश्लेषण', 'Health & Vulnerability Analysis')}
          </h1>
          <p className="mt-1 text-xs text-stone-400">
            {t(
              'आयुर्वेदिक प्रकृति, शारीरिक संवेदनशीलता, एवं प्रकृति-विशिष्ट जीवन-शैली निर्देश।',
              'Ayurvedic prakṛti, body-area sensitivity, and constitution-specific lifestyle guidance.',
            )}
          </p>
        </div>
        <div className="flex shrink-0 overflow-hidden rounded-lg ring-1 ring-stone-700">
          <button
            type="button"
            onClick={() => setLang('hi')}
            className={
              'px-3 py-1.5 text-xs font-medium transition '
              + (lang === 'hi'
                ? 'bg-saffron-600 text-stone-950'
                : 'bg-stone-800/60 text-stone-300 hover:bg-stone-800')
            }
            aria-pressed={lang === 'hi'}
          >
            हिन्दी
          </button>
          <button
            type="button"
            onClick={() => setLang('en')}
            className={
              'px-3 py-1.5 text-xs font-medium transition '
              + (lang === 'en'
                ? 'bg-saffron-600 text-stone-950'
                : 'bg-stone-800/60 text-stone-300 hover:bg-stone-800')
            }
            aria-pressed={lang === 'en'}
          >
            English
          </button>
        </div>
      </header>

      <ChartInputForm
        onSubmit={onFormSubmit}
        isSubmitting={reportMut.isPending}
        submitError={submitError}
      />

      {reportMut.isPending && (
        <p className="rounded-2xl bg-stone-900/40 p-8 text-center text-stone-400 ring-1 ring-stone-800">
          {t('विश्लेषण हो रहा है…', 'Analysing…')}
        </p>
      )}

      {report && (
        <ReportView
          report={report}
          lang={lang}
          t={t}
          onDownloadPdf={downloadPdf}
          pdfDownloading={pdfDownloading}
        />
      )}
    </div>
  );
}

function ReportView({
  report,
  lang,
  t,
  onDownloadPdf,
  pdfDownloading,
}: {
  report: HealthVulnerabilityResponse;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
  onDownloadPdf: () => void;
  pdfDownloading: boolean;
}) {
  return (
    <article className="space-y-4">
      {/* Disclaimer banner — top */}
      <DisclaimerBanner
        text={lang === 'hi' ? report.disclaimer_hi : report.disclaimer_en}
      />

      {/* Header card with PDF download */}
      <section className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-gradient-to-br from-saffron-900/50 to-stone-900 p-5 ring-2 ring-saffron-700/50">
        <div>
          <p className="text-xs uppercase tracking-wider text-saffron-300">
            {t('स्वास्थ्य रिपोर्ट', 'Health Report')}
          </p>
          <p className="mt-1 text-xl font-semibold text-saffron-100">
            {report.input.name}
          </p>
          <p className="mt-0.5 text-xs text-stone-400">
            {report.input.dob} · {report.input.tob} ·{' '}
            {lang === 'hi' ? report.lagna.rashi_name_hi : report.lagna.rashi_name_en}{' '}
            {t('लग्न', 'Ascendant')}
          </p>
        </div>
        <button
          type="button"
          onClick={onDownloadPdf}
          disabled={pdfDownloading}
          className="rounded-xl bg-saffron-600 px-4 py-2 text-sm font-medium text-stone-950 hover:bg-saffron-500 disabled:opacity-50"
        >
          {pdfDownloading
            ? t('बन रहा है…', 'Generating…')
            : t('PDF डाउनलोड', 'Download PDF')}
        </button>
      </section>

      {/* Layer 1 — Takeaways */}
      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <h2 className="mb-1 text-base font-semibold text-saffron-200">
          {t('मुख्य निष्कर्ष', 'Key Takeaways')}
        </h2>
        <p className="mb-3 text-xs text-stone-500">
          {t('सरल भाषा में', 'In plain language')}
        </p>
        <ul className="space-y-2 text-sm leading-relaxed text-stone-100">
          {(lang === 'hi' ? report.takeaways_hi : report.takeaways_en).map((tk, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-saffron-400">●</span>
              <span>{tk}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Prakṛti — constitutional analysis */}
      <PrakritiCard prakriti={report.prakriti} lang={lang} t={t} />

      {/* Vulnerability map */}
      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <h2 className="mb-1 text-base font-semibold text-saffron-200">
          {t('शरीर-संवेदनशीलता मानचित्र', 'Body-Area Sensitivity Map')}
        </h2>
        <p className="mb-3 text-xs text-stone-500">
          {t('12 भाव × शरीर-तंत्र — स्वच्छ, ध्यान, सतर्कता।',
             '12 houses × body systems — clear, watch, caution.')}
        </p>
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {report.vulnerability_map.map((row, i) => (
            <VulnerabilityCell row={row} lang={lang} key={i} />
          ))}
        </ul>
      </section>

      {/* Mental health */}
      {report.mental_lines.length > 0 && (
        <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
          <h2 className="mb-1 text-base font-semibold text-saffron-200">
            {t('मानसिक एवं भावनात्मक स्वास्थ्य', 'Mental & Emotional Health')}
          </h2>
          <p className="mb-3 text-xs text-stone-400">
            {lang === 'hi' ? report.mental_summary_hi : report.mental_summary_en}
          </p>
          <div className="space-y-2">
            {report.mental_lines.map((l, i) => (
              <HealthLineCard line={l} lang={lang} key={i} />
            ))}
          </div>
        </section>
      )}

      {/* Chronic risk */}
      {report.chronic_lines.length > 0 && (
        <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
          <h2 className="mb-3 text-base font-semibold text-saffron-200">
            {t('दीर्घ-कालिक जोखिम', 'Chronic Risk')}
          </h2>
          <div className="space-y-2">
            {report.chronic_lines.map((l, i) => (
              <HealthLineCard line={l} lang={lang} key={i} />
            ))}
          </div>
        </section>
      )}

      {/* Dasha + transits */}
      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <h2 className="mb-1 text-base font-semibold text-saffron-200">
          {t('दशा-काल एवं गोचर', 'Dasha & Transits')}
        </h2>
        <p className="mb-3 text-sm leading-relaxed text-stone-100">
          {lang === 'hi' ? report.dasha_view_hi : report.dasha_view_en}
        </p>
        {report.transit_watchlist.length > 0 && (
          <>
            <p className="mt-3 mb-1 text-[10px] uppercase tracking-wider text-stone-500">
              {t('12-माह सतर्कता', '12-Month Watchlist')}
            </p>
            <div className="space-y-2">
              {report.transit_watchlist.map((l, i) => (
                <HealthLineCard line={l} lang={lang} key={i} />
              ))}
            </div>
          </>
        )}
      </section>

      {/* Lifestyle prescriptions */}
      <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
        <h2 className="mb-1 text-base font-semibold text-saffron-200">
          {t('जीवन-शैली निर्देश', 'Lifestyle Prescriptions')}
        </h2>
        <p className="mb-3 text-xs text-stone-500">
          {t(
            'आपकी प्रकृति के अनुसार — पारम्परिक आचार्य-शास्त्रों से।',
            'Tailored to your prakṛti — drawn from classical āchārya texts.',
          )}
        </p>
        <div className="space-y-3">
          {report.lifestyle_prescriptions.map((p, i) => (
            <PrescriptionCard p={p} lang={lang} key={i} />
          ))}
        </div>
      </section>

      {/* Aṣṭāṅga Hṛdayam — Sūtrasthāna 2 (Dinacaryā) */}
      {report.ah_dinacharya && (
        <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
          <h2 className="mb-1 text-base font-semibold text-saffron-200">
            {t(
              'दिनचर्या — अष्टांग हृदयम्, सूत्रस्थान २',
              'Dinacaryā — Aṣṭāṅga Hṛdayam, Sūtrasthāna 2',
            )}
          </h2>
          <p className="mb-3 text-xs text-stone-500">
            {t(
              'दैनिक आचरण-निर्देश, आचार्य वाग्भट के मूल श्लोक-संदर्भों के साथ।',
              "Daily-routine prescriptions with verse references from Ācārya Vāgbhaṭa's text.",
            )}
          </p>
          <div className="space-y-3">
            {report.ah_dinacharya.universal_prescriptions.map((p, i) => (
              <AhPrescriptionCard p={p} lang={lang} key={`u-${i}`} />
            ))}
            {report.ah_dinacharya.prakriti_specific_prescriptions.length > 0 && (
              <>
                <h3 className="mt-4 text-xs font-semibold uppercase tracking-wider text-saffron-300">
                  {t('आपकी प्रकृति-विशेष', 'Specific to your Prakṛti')}
                </h3>
                {report.ah_dinacharya.prakriti_specific_prescriptions.map((p, i) => (
                  <AhPrescriptionCard p={p} lang={lang} key={`s-${i}`} />
                ))}
              </>
            )}
          </div>
        </section>
      )}

      {/* Aṣṭāṅga Hṛdayam — Sūtrasthāna 3 (Ṛtucaryā) */}
      {report.ah_ritucharya && (
        <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
          <header className="mb-3 flex items-baseline justify-between">
            <h2 className="text-base font-semibold text-saffron-200">
              {t(
                'ऋतुचर्या — अष्टांग हृदयम्, सूत्रस्थान ३',
                'Ṛtucaryā — Aṣṭāṅga Hṛdayam, Sūtrasthāna 3',
              )}
            </h2>
            <span className="rounded-full bg-saffron-900/40 px-2 py-0.5 text-[10px] font-medium text-saffron-200">
              {lang === 'hi'
                ? report.ah_ritucharya.active_season_hi
                : report.ah_ritucharya.active_season_en}
            </span>
          </header>
          <p className="mb-3 rounded-lg bg-stone-900/40 p-2 text-xs italic leading-relaxed text-stone-300">
            {lang === 'hi'
              ? report.ah_ritucharya.active_dosha_status_hi
              : report.ah_ritucharya.active_dosha_status_en}
          </p>
          <div className="space-y-3">
            {report.ah_ritucharya.active_prescriptions.map((p, i) => (
              <AhPrescriptionCard p={p} lang={lang} key={i} />
            ))}
          </div>
          <details className="mt-4 rounded-lg bg-stone-900/30 p-3 ring-1 ring-stone-800">
            <summary className="cursor-pointer text-xs font-medium text-saffron-200">
              {t('षड्ऋतु-चक्र अवलोकन', 'Six-Ṛtu Cycle Overview')}
            </summary>
            <p className="mt-2 text-xs leading-relaxed text-stone-300">
              {lang === 'hi'
                ? report.ah_ritucharya.six_season_overview_hi
                : report.ah_ritucharya.six_season_overview_en}
            </p>
          </details>
        </section>
      )}

      {/* Annual recap */}
      <section className="rounded-2xl bg-gradient-to-br from-saffron-900/40 to-stone-900 p-5 ring-1 ring-saffron-700/40">
        <h2 className="mb-2 text-base font-semibold text-saffron-200">
          {t('वार्षिक सारांश', 'Annual Recap')}
        </h2>
        <p className="text-sm leading-relaxed text-stone-100">
          {lang === 'hi' ? report.annual_recap_hi : report.annual_recap_en}
        </p>
      </section>

      {/* Methodology footer — financial-disclaimer tier */}
      <section className="rounded-2xl bg-stone-900/20 p-3 ring-1 ring-stone-800/40">
        <h3 className="mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-stone-500">
          {t('प्रयुक्त ज्योतिष-आयुर्वेद विधि', 'Methodology Used')}
        </h3>
        <ul className="space-y-0.5 text-[8.5px] leading-snug text-stone-500">
          {report.methods.map((m, i) => (
            <li key={i}>
              <span className="text-stone-400">
                {lang === 'hi' ? m.method_hi : m.method_en}
              </span>
              <span className="text-stone-600">
                {' — '}
                {lang === 'hi' ? m.source_hi : m.source_en}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-2 border-t border-stone-800/40 pt-1.5 text-[8.5px] italic leading-snug text-stone-600">
          {lang === 'hi' ? report.disclaimer_hi : report.disclaimer_en}
        </p>
        <p className="mt-1 text-[8px] text-stone-700">{report.rules_version}</p>
      </section>
    </article>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

function DisclaimerBanner({ text }: { text: string }) {
  return (
    <div className="rounded-2xl bg-amber-950/30 p-3 ring-1 ring-amber-800/50">
      <p className="text-xs leading-relaxed text-amber-100">{text}</p>
    </div>
  );
}

function PrakritiCard({
  prakriti,
  lang,
  t,
}: {
  prakriti: PrakritiBreakdownEntry;
  lang: 'hi' | 'en';
  t: (hi: string, en: string) => string;
}) {
  return (
    <section className="rounded-2xl bg-stone-950/40 p-5 ring-1 ring-stone-800">
      <h2 className="mb-1 text-base font-semibold text-saffron-200">
        {t('आपकी प्रकृति', 'Your Prakṛti')}
      </h2>
      <p className="mb-3 text-lg font-semibold text-saffron-100">
        {lang === 'hi' ? prakriti.label_hi : prakriti.label_en}
      </p>

      {/* Stacked bar */}
      <div className="flex h-3 overflow-hidden rounded-full ring-1 ring-stone-800">
        <div
          className="bg-sky-500/70"
          style={{ width: `${prakriti.vata_pct}%` }}
          title={`Vata ${prakriti.vata_pct}%`}
        />
        <div
          className="bg-rose-500/70"
          style={{ width: `${prakriti.pitta_pct}%` }}
          title={`Pitta ${prakriti.pitta_pct}%`}
        />
        <div
          className="bg-emerald-600/70"
          style={{ width: `${prakriti.kapha_pct}%` }}
          title={`Kapha ${prakriti.kapha_pct}%`}
        />
      </div>
      <div className="mt-2 flex justify-between text-[11px] text-stone-400">
        <span className="text-sky-300">● {t('वात', 'Vata')} {prakriti.vata_pct}%</span>
        <span className="text-rose-300">● {t('पित्त', 'Pitta')} {prakriti.pitta_pct}%</span>
        <span className="text-emerald-300">● {t('कफ', 'Kapha')} {prakriti.kapha_pct}%</span>
      </div>

      <p className="mt-4 text-sm leading-relaxed text-stone-100">
        {lang === 'hi' ? prakriti.description_hi : prakriti.description_en}
      </p>
    </section>
  );
}

function VulnerabilityCell({
  row,
  lang,
}: {
  row: VulnerabilityRowEntry;
  lang: 'hi' | 'en';
}) {
  const colour = {
    clear:   'bg-emerald-900/20 text-emerald-100 ring-emerald-800/40',
    watch:   'bg-amber-900/20 text-amber-100 ring-amber-800/40',
    caution: 'bg-rose-900/20 text-rose-100 ring-rose-800/40',
  }[row.status];
  return (
    <li className={'rounded-md px-3 py-2 ring-1 ' + colour}>
      <p className="text-xs font-medium">
        {lang === 'hi' ? row.body_area_hi : row.body_area_en}
      </p>
      <p className="mt-0.5 text-xs opacity-80">
        {lang === 'hi' ? row.note_hi : row.note_en}
      </p>
    </li>
  );
}

function HealthLineCard({
  line,
  lang,
}: {
  line: HealthLineEntry;
  lang: 'hi' | 'en';
}) {
  const borderColour = {
    info:    'border-stone-700/60',
    watch:   'border-amber-600/60',
    caution: 'border-rose-600/60',
  }[line.severity];
  return (
    <div className={'rounded-md border-l-2 bg-stone-900/40 px-3 py-2 ' + borderColour}>
      <p className="text-sm font-medium text-saffron-300">
        {lang === 'hi' ? line.label_hi : line.label_en}
      </p>
      <p className="mt-1 text-sm leading-relaxed text-stone-100">
        {lang === 'hi' ? line.text_hi : line.text_en}
      </p>
      {(line.fired_by_hi || line.fired_by_en) && (
        <p className="mt-1 text-[10px] italic text-stone-500">
          {lang === 'hi' ? line.fired_by_hi : line.fired_by_en}
        </p>
      )}
    </div>
  );
}

function PrescriptionCard({
  p,
  lang,
}: {
  p: LifestylePrescriptionEntry;
  lang: 'hi' | 'en';
}) {
  return (
    <div className="rounded-md border-l-2 border-saffron-700/60 bg-stone-900/40 px-3 py-2.5">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-saffron-300">
        {lang === 'hi' ? p.category_hi : p.category_en}
      </p>
      <p className="mt-1 text-sm leading-relaxed text-stone-100">
        {lang === 'hi' ? p.text_hi : p.text_en}
      </p>
    </div>
  );
}

function AhPrescriptionCard({
  p,
  lang,
}: {
  p: AhPrescriptionEntry;
  lang: 'hi' | 'en';
}) {
  return (
    <div className="rounded-md border-l-2 border-amber-700/60 bg-stone-900/40 px-3 py-2.5">
      <header className="flex items-baseline justify-between gap-2">
        <p className="text-xs font-semibold text-amber-200">
          {lang === 'hi' ? p.title_hi : p.title_en}
        </p>
        <span className="shrink-0 text-[10px] font-medium tabular-nums text-stone-500">
          {p.ref.label}
        </span>
      </header>
      <p className="mt-1 text-sm leading-relaxed text-stone-100">
        {lang === 'hi' ? p.text_hi : p.text_en}
      </p>
    </div>
  );
}
